import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import type { FunctionReturnType } from "convex/server";
import {
  LuArrowRight,
  LuArrowUpRight,
  LuBell,
  LuCircleCheck,
  LuClock3,
  LuGraduationCap,
  LuMessagesSquare,
  LuPenLine,
  LuTriangleAlert,
  LuUsersRound,
} from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { PageHeader } from "../components/PageHeader";
import { ProgressBar } from "../components/ProgressRows";
import { useEffectivePersonId } from "../impersonation";
import { isActivityOverdue, viennaToday } from "@/lib/activityDates";
import { formatDay } from "./ActivityAdminShared";

/**
 * The dashboard.
 *
 * It used to open on how many people are in the directory and how many groups
 * exist -- true, and no business of the person reading it. A dashboard answers
 * one question: what do I need to do. Everything here is about the viewer's
 * own work, and every panel that has nothing to say is absent rather than
 * empty, so a PhD student and a group head get different pages without either
 * choosing a mode.
 *
 * Built from the queries the other pages already use rather than a dashboard
 * query of its own: every one of them already follows impersonation and
 * already enforces its own permissions, and a second implementation of
 * "what is this person waiting on" is a second thing to get wrong.
 */

type Assignment = FunctionReturnType<typeof api.personActivities.listForViewer>[number];
type Programme = FunctionReturnType<typeof api.programmeProgress.forViewer>;

function relativeDay(day: string | null, today: string): string {
  if (day === null) {
    return "No date";
  }
  if (day === today) {
    return "Today";
  }

  const diff = Math.round(
    (new Date(`${day}T12:00:00Z`).getTime() - new Date(`${today}T12:00:00Z`).getTime())
    / 86_400_000,
  );

  if (diff === 1) {
    return "Tomorrow";
  }
  if (diff === -1) {
    return "Yesterday";
  }
  if (diff < 0) {
    return `${Math.abs(diff)} days ago`;
  }
  if (diff < 30) {
    return `In ${diff} days`;
  }
  return formatDay(day);
}

// The routes a panel's "see everything" link can point at. Spelled out rather
// than typed as string so the router still checks them.
type PanelRoute =
  | "/notifications"
  | "/activities"
  | "/activities/reviews"
  | "/my-programme"
  | "/my-group"
  | "/cemm-chat";

/** A panel, so that the six of them look like one thing. */
function Panel(props: {
  title: string;
  icon: typeof LuBell;
  tone?: string;
  action?: { label: string; to: PanelRoute };
  children: React.ReactNode;
}) {
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={4} p={6}>
        <Flex align="center" justify="space-between" gap={4}>
          <HStack gap={3}>
            <Icon as={props.icon} boxSize={5} color={props.tone ?? "blue.500"} />
            <Heading size="md">{props.title}</Heading>
          </HStack>
          {props.action === undefined ? null : (
            <Button asChild variant="ghost" size="sm" colorPalette="blue">
              <Link to={props.action.to}>
                {props.action.label}
                <LuArrowUpRight />
              </Link>
            </Button>
          )}
        </Flex>
        {props.children}
      </Card.Body>
    </Card.Root>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={4} py={4}>
      <Text fontSize="sm" color="fg.muted">{children}</Text>
    </Box>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const effectivePersonId = useEffectivePersonId();
  const personArg = { effectivePersonId: effectivePersonId as Id<"persons"> | null };

  const viewerProfile = useQuery(api.persons.viewerProfile, personArg);
  const notifications = useQuery(api.notifications.get, personArg);
  const assignments = useQuery(api.personActivities.listForViewer, personArg) as
    | Assignment[]
    | undefined;
  const programme = useQuery(api.programmeProgress.forViewer, {
    ...personArg,
    personId: null,
  }) as Programme | undefined;
  const queue = useQuery(api.reviews.supervisorQueue, personArg);
  const usage = useQuery(api.chatUsage.myUsageCells, {
    ...personArg,
    // The calendar month, as the CeMM Chat page counts it: a rolling thirty
    // days matched nothing anybody is billed or asked about.
    fromDay: `${viennaToday().slice(0, 8)}01`,
    toDay: viennaToday(),
    bucket: "day" as const,
  });

  const today = viennaToday();

  // Reviewers are notified when something arrives for them to sign, which is
  // what puts it in the email digest. That would otherwise list the same work
  // here twice -- once as a notification and once in "Waiting on you" below.
  //
  // The live panel wins. It is computed from the queue, so an item leaves it
  // the moment it is signed; a notification is a historical event that sits
  // there until somebody reads it, and can still be asking for a signature
  // that was given yesterday.
  const reviewingActivityIds = useMemo(
    () => new Set((queue?.pendingReviews ?? []).map((row) => String(row.personActivityId))),
    [queue],
  );

  // Only the ones that ask for something, and only the unread ones: a
  // notification that has been opened has been dealt with, which is the rule
  // the notifications page already applies.
  const actionable = useMemo(
    () => (notifications ?? []).filter((item) =>
      !item.isRead
      && item.category === "action_required"
      && !(item.entityId !== undefined && reviewingActivityIds.has(String(item.entityId)))),
    [notifications, reviewingActivityIds],
  );

  // Required work only. Encouraged activities are real work that never counts
  // as late (model §7), and a dashboard that chased somebody about an optional
  // patent application would be worse than one that said nothing. They are
  // counted separately below instead.
  const live = useMemo(
    () => (assignments ?? []).filter((row) =>
      row.status !== "completed"
      && row.status !== "archived"
      && row.status !== "cancelled"),
    [assignments],
  );

  const dueSoonest = useMemo(() => {
    const required = live.filter((row) => row.required && row.dueDay !== null);
    return [...required]
      .sort((left, right) => (left.dueDay ?? "").localeCompare(right.dueDay ?? ""))
      .slice(0, 5);
  }, [live]);

  const overdueCount = useMemo(
    () => live.filter((row) => isActivityOverdue(row, today)).length,
    [live, today],
  );
  const encouragedCount = useMemo(
    () => live.filter((row) => !row.required).length,
    [live],
  );

  // Heads only. A group panel for somebody who heads nothing is a panel about
  // somebody else's group.
  const headedGroups = useMemo(
    () => (viewerProfile?.affiliations ?? []).filter((affiliation) =>
      affiliation.isCurrent
      && affiliation.headRole !== null
      && affiliation.orgUnit !== null),
    [viewerProfile],
  );

  // Day cells, so today is a slice of the month rather than a second query.
  const usageTotals = useMemo(() => {
    const cost = (cells: NonNullable<typeof usage>) => cells.reduce((sum, cell) => sum + cell.costUsd, 0);
    const cells = usage ?? [];
    return {
      month: cost(cells),
      today: cost(cells.filter((cell) => cell.bucket === today)),
    };
  }, [usage, today]);

  const pendingReviews = queue?.pendingReviews ?? [];
  const greetingName =
    viewerProfile?.person?.firstName ?? viewerProfile?.auth?.name?.split(" ")[0] ?? null;

  const currentProgrammeYear = programme?.years.find((year) => year.isCurrent) ?? null;

  const nothingWaiting =
    actionable.length === 0 && dueSoonest.length === 0 && pendingReviews.length === 0;

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={greetingName ? `Welcome back, ${greetingName}` : "Welcome to CeMMbase"}
        description="What is waiting for you, and what is coming up."
        breadcrumbs={[{ label: "CeMMbase" }]}
        loading={viewerProfile === undefined}
      />

      <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {nothingWaiting && assignments !== undefined ? (
          <Card.Root borderWidth="1px" borderColor="green.300">
            <Card.Body p={6}>
              <HStack gap={3}>
                <Icon as={LuCircleCheck} boxSize={5} color="green.500" />
                <Text fontSize="sm">
                  Nothing is waiting for you. Anything new will appear here and in your
                  notifications.
                </Text>
              </HStack>
            </Card.Body>
          </Card.Root>
        ) : null}

        <Grid gap={6} templateColumns={{ base: "1fr", xl: "1.25fr 1fr" }} alignItems="start">
          <Stack gap={6}>
            <Panel
              title="Needs you"
              icon={LuBell}
              tone="orange.500"
              action={{ label: "All notifications", to: "/notifications" }}
            >
              {notifications === undefined ? (
                <EmptyLine>Loading…</EmptyLine>
              ) : actionable.length === 0 ? (
                <EmptyLine>Nothing is asking for your attention.</EmptyLine>
              ) : (
                <Stack gap={2}>
                  {actionable.slice(0, 5).map((item) => (
                    <Flex
                      key={item._id}
                      align="flex-start"
                      justify="space-between"
                      gap={4}
                      borderWidth="1px"
                      borderColor="border"
                      p={4}
                      cursor={item.actionUrl ? "pointer" : undefined}
                      _hover={item.actionUrl ? { borderColor: "blue.400" } : undefined}
                      onClick={() => {
                        if (item.actionUrl) {
                          void navigate({ to: item.actionUrl });
                        }
                      }}
                    >
                      <Box minW={0}>
                        <Text fontWeight="semibold" fontSize="sm">{item.title}</Text>
                        <Text mt={1} fontSize="sm" color="fg.muted">{item.message}</Text>
                      </Box>
                      {item.actionUrl ? (
                        <Icon as={LuArrowRight} boxSize={4} color="fg.muted" flexShrink={0} mt={1} />
                      ) : null}
                    </Flex>
                  ))}
                  {actionable.length > 5 ? (
                    <Text fontSize="xs" color="fg.muted">
                      …and {actionable.length - 5} more.
                    </Text>
                  ) : null}
                </Stack>
              )}
            </Panel>

            <Panel
              title="Due soonest"
              icon={LuClock3}
              tone={overdueCount > 0 ? "red.500" : "blue.500"}
              action={{ label: "All activities", to: "/activities" }}
            >
              {assignments === undefined ? (
                <EmptyLine>Loading…</EmptyLine>
              ) : dueSoonest.length === 0 ? (
                <EmptyLine>Nothing with a deadline is outstanding.</EmptyLine>
              ) : (
                <Stack gap={2}>
                  {overdueCount > 0 ? (
                    <HStack gap={2} color="red.600">
                      <Icon as={LuTriangleAlert} boxSize={4} />
                      <Text fontSize="sm" fontWeight="semibold">
                        {overdueCount} overdue
                      </Text>
                    </HStack>
                  ) : null}
                  {dueSoonest.map((row) => {
                    const overdue = isActivityOverdue(row, today);
                    return (
                      <Flex
                        key={row._id}
                        align="center"
                        justify="space-between"
                        gap={4}
                        borderWidth="1px"
                        borderColor={overdue ? "red.300" : "border"}
                        p={4}
                        cursor="pointer"
                        _hover={{ borderColor: "blue.400" }}
                        onClick={() => {
                          void navigate({
                            to: "/activities/$personActivityId",
                            params: { personActivityId: row._id },
                          });
                        }}
                      >
                        <Box minW={0}>
                          <Text fontWeight="semibold" fontSize="sm">{row.titleSnapshot}</Text>
                          <Text mt={1} fontSize="xs" color="fg.muted">{row.statusLabel}</Text>
                        </Box>
                        <Badge
                          flexShrink={0}
                          colorPalette={overdue ? "red" : "gray"}
                          variant={overdue ? "solid" : "subtle"}
                        >
                          {relativeDay(row.dueDay, today)}
                        </Badge>
                      </Flex>
                    );
                  })}
                  {encouragedCount > 0 ? (
                    <Text fontSize="xs" color="fg.muted">
                      Plus {encouragedCount} encouraged {encouragedCount === 1 ? "activity" : "activities"},
                      which never fall due.
                    </Text>
                  ) : null}
                </Stack>
              )}
            </Panel>
          </Stack>

          <Stack gap={6}>
            {programme === undefined || programme === null ? null : (
              <Panel
                title="Your programme"
                icon={LuGraduationCap}
                tone="purple.500"
                action={{ label: "Full record", to: "/my-programme" }}
              >
                <SimpleGrid columns={2} gap={4}>
                  <Box>
                    <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.16em">
                      Year
                    </Text>
                    <Text fontSize="2xl" fontWeight="semibold">{programme.currentYear}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.16em">
                      Cohort
                    </Text>
                    <Text fontSize="2xl" fontWeight="semibold">
                      {programme.cohort ?? "—"}
                    </Text>
                  </Box>
                </SimpleGrid>
                {currentProgrammeYear === null ? null : (
                  <Box>
                    <Text mb={2} fontSize="xs" color="fg.muted">
                      Year {currentProgrammeYear.year}
                    </Text>
                    <ProgressBar rows={currentProgrammeYear.rows} />
                  </Box>
                )}
              </Panel>
            )}

            {pendingReviews.length === 0 ? null : (
              <Panel
                title="Waiting on you"
                icon={LuPenLine}
                tone="teal.500"
                action={{ label: "Review queue", to: "/activities/reviews" }}
              >
                <Stack gap={2}>
                  {pendingReviews.slice(0, 4).map((review) => (
                    <Flex
                      key={review.taskId}
                      align="center"
                      justify="space-between"
                      gap={4}
                      borderWidth="1px"
                      borderColor="border"
                      p={4}
                      cursor="pointer"
                      _hover={{ borderColor: "blue.400" }}
                      onClick={() => {
                        void navigate({
                          to: "/activities/$personActivityId",
                          params: { personActivityId: review.personActivityId },
                          search: { from: "dashboard" },
                        });
                      }}
                    >
                      <Box minW={0}>
                        <Text fontWeight="semibold" fontSize="sm">{review.taskTitle}</Text>
                        <Text mt={1} fontSize="xs" color="fg.muted">
                          {review.personName} · {review.activityTitle}
                        </Text>
                      </Box>
                      {/* Two different jobs: signing off work somebody else
                          submitted, versus filling in a step that is yours to
                          do. Saying which saves opening it to find out. */}
                      <Badge
                        flexShrink={0}
                        colorPalette={review.kind === "approve" ? "teal" : "purple"}
                        variant="subtle"
                      >
                        {review.kind === "approve" ? "Sign off" : "Your step"}
                      </Badge>
                    </Flex>
                  ))}
                  {pendingReviews.length > 4 ? (
                    <Text fontSize="xs" color="fg.muted">
                      …and {pendingReviews.length - 4} more.
                    </Text>
                  ) : null}
                </Stack>
              </Panel>
            )}

            {headedGroups.length === 0 ? null : (
              <Panel
                title={headedGroups.length === 1 ? "Your group" : "Your groups"}
                icon={LuUsersRound}
                tone="cyan.500"
                action={{ label: "Open my group", to: "/my-group" }}
              >
                <Stack gap={3}>
                  {headedGroups.map((affiliation) => (
                    <Text key={affiliation.orgUnit!._id} fontWeight="semibold" fontSize="sm">
                      {affiliation.orgUnit!.name}
                      <Text as="span" ml={2} fontSize="xs" color="fg.muted" fontWeight="normal">
                        {affiliation.roleLabel}
                      </Text>
                    </Text>
                  ))}
                  <SimpleGrid columns={3} gap={3}>
                    <Box>
                      <Text fontSize="xl" fontWeight="semibold">{queue?.supervisedPeople ?? 0}</Text>
                      <Text fontSize="xs" color="fg.muted">people supervised</Text>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xl"
                        fontWeight="semibold"
                        color={(queue?.overdueActivities ?? 0) > 0 ? "red.600" : undefined}
                      >
                        {queue?.overdueActivities ?? 0}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">activities overdue</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xl" fontWeight="semibold">{queue?.blockedActivities ?? 0}</Text>
                      <Text fontSize="xs" color="fg.muted">activities blocked</Text>
                    </Box>
                  </SimpleGrid>
                </Stack>
              </Panel>
            )}

            {/* Shown at zero too: an empty month is an answer, and a panel
                that comes and goes moves everything below it. */}
            {usage === undefined ? null : (
              <Panel
                title="CeMM-Chat"
                icon={LuMessagesSquare}
                tone="pink.500"
                action={{ label: "Details", to: "/cemm-chat" }}
              >
                {/* Cost only: requests and tokens are on the CeMM Chat page,
                    and what it costs is the figure people check. Today first,
                    as the more immediate of the two. */}
                <SimpleGrid columns={2} gap={3}>
                  {([
                    ["today", usageTotals.today],
                    ["this month", usageTotals.month],
                  ] as const).map(([label, costUsd]) => (
                    <Box key={label}>
                      <Text fontSize="xl" fontWeight="semibold">${costUsd.toFixed(2)}</Text>
                      <Text fontSize="xs" color="fg.muted">{label}</Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Panel>
            )}
          </Stack>
        </Grid>
      </Stack>
    </Box>
  );
}
